import { gettext } from '@eventbrite/i18n';
import React, { useEffect, useRef } from 'react';
import { useCreatorCollections } from '../../app';
import { STATSIG_EVENT_NAMES } from '../../constants';
import { Trackeable } from '../ExpiredEvents/ExpiredEventsPage';
import { useRelatedEvents } from '../RelatedEvents/RelatedEvents';
import { EventsList as ListingsEventsList } from '../_shared/EventsList/EventsList';

export const EnhancedExpiredEventsList: React.FunctionComponent<{
    isAuthenticated: boolean;
    locale: string;
    affCode: string;
    eventId: string;
    organizer: {
        name: string;
        profilePicture?: string;
        id: string;
    };
    launchLinkToCreatorCollectionsOnListing: boolean;
    isExpiredEvent: boolean;
    forceTwoColumnLayout?: boolean;
    isSalesEndedListingExperimentEnabled: boolean;
    scrollToRelatedEvents: boolean;
}> = ({
    isAuthenticated,
    locale,
    affCode,
    eventId,
    organizer,
    launchLinkToCreatorCollectionsOnListing,
    isExpiredEvent,
    forceTwoColumnLayout,
    isSalesEndedListingExperimentEnabled,
    scrollToRelatedEvents,
}) => {
    const ref = useRef<null | HTMLDivElement>(null);
    const creatorCollections = useCreatorCollections(
        eventId,
        launchLinkToCreatorCollectionsOnListing,
        isExpiredEvent,
        isSalesEndedListingExperimentEnabled,
    );

    const relatedEvents = useRelatedEvents(
        organizer.id,
        eventId,
        isAuthenticated,
        creatorCollections?.length || 0,
        '',
        isSalesEndedListingExperimentEnabled,
    );

    const promotedEvents =
        relatedEvents?.eventsOtherOrganizers?.promotedEvents || [];
    const filteredEvents =
        relatedEvents?.eventsOtherOrganizers?.filteredEvents || [];
    const sameOrganizerEvents = relatedEvents.eventsSameOrganizer || [];
    const allRelatedEvents = [...promotedEvents, ...filteredEvents];
    const allEvents = [...sameOrganizerEvents, ...allRelatedEvents];

    useEffect(() => {
        if (scrollToRelatedEvents) {
            ref.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [scrollToRelatedEvents]);

    const eventCards =
        relatedEvents.eventsSameOrganizer?.length || allRelatedEvents.length ? (
            <div ref={ref} data-testid="enhanced-expired-events-list">
                {relatedEvents.eventsSameOrganizer?.length ? (
                    <ListingsEventsList
                        statsigEventMetrics={{
                            eventClick:
                                STATSIG_EVENT_NAMES.LISTING_MORE_ORGANIZER_EVENT_CLICK,
                            eventLike:
                                STATSIG_EVENT_NAMES.LISTING_MORE_ORGANIZER_EVENT_LIKE,
                        }}
                        isAuthenticated={isAuthenticated}
                        locale={locale}
                        affCode={affCode}
                        title={gettext('More %(organizerName)s events', {
                            organizerName: organizer.name,
                        }).toString()}
                        events={relatedEvents.eventsSameOrganizer}
                        forceTwoColumnLayout={forceTwoColumnLayout}
                    />
                ) : null}

                {allRelatedEvents.length ? (
                    <ListingsEventsList
                        statsigEventMetrics={{
                            eventClick:
                                STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_CLICK,
                            eventLike:
                                STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_LIKE,
                        }}
                        isAuthenticated={isAuthenticated}
                        locale={locale}
                        affCode={affCode}
                        title={gettext('Other events you may like').toString()}
                        events={allRelatedEvents}
                        forceTwoColumnLayout={forceTwoColumnLayout}
                    />
                ) : null}
            </div>
        ) : null;

    return promotedEvents.length ? (
        <Trackeable events={allEvents}>{eventCards}</Trackeable>
    ) : (
        eventCards
    );
};
